import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalSizes, ButtonVariants, Table } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader, resolveRowUnitType } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { buildTableLabels } from '@/config/tableLabels';
import { buildPickColumn } from '../tableCells';
import { buildFulfillPickerFilterDefinitions } from '@/config/filterDefinitions';
import { DiscrepancyModal } from '../Discrepancy';

const getRowId = (row) => row.id;

/**
 * Props:
 *   open    whether the modal is shown
 *   onClose close handler (✕ / Cancel)
 *   config  the same config <SelectFromTable> takes — this reads its modal half
 *   rows    the row view models the table lists
 *   total   total row count behind the server pagination
 *   loading whether the list is still fetching
 *   onSearchChange / onFilterChange / onSortChange / onPageChange / onPageSizeChange
 *   onConfirm (rows, unitTypes) => void — unitTypes is rowId → the unit it was showing
 */
export const PickFromTableModal = ({
  open,
  onClose,
  config,
  rows = EMPTY_LIST,
  total = 0,
  loading = false,
  onSearchChange,
  onFilterChange,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onConfirm,
}) => {
  const t = useTranslations();
  const {
    name = 'selection',
    titleKey,
    emptyKey,
    confirmKey = 'COMMON_SELECT',
    selectMode = 'single',
    maxSelected,
    filterable,
    buildColumns,
    unitOptions,
    buildDiscrepancy,
    buildGroups,
    getRowGroup,
  } = config;
  const isSingle = selectMode === 'single';
  const filters = useMemo(
    () => (filterable ? buildFulfillPickerFilterDefinitions(t) : undefined),
    [filterable, t],
  );

  const [pickedId, setPickedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState(EMPTY_LIST);
  // The Table owns its search, selection and page, so only a remount clears them.
  const [session, setSession] = useState(0);
  const [discrepancyRow, setDiscrepancyRow] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPickedId(null);
    setCheckedIds(EMPTY_LIST);
    setDiscrepancyRow(null);
    setSession((previous) => previous + 1);
    onSearchChange?.('');
    onFilterChange?.({});
    // The handlers are the parent's setters; re-running on their identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(rows, unitOptions, t);

  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handlePick = useCallback((id) => setPickedId(id), []);
  const handleSelectionChange = useCallback((selectedRows, ids) => setCheckedIds(ids), []);
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);
  const handleSearchChange = useCallback((value) => onSearchChange?.(value), [onSearchChange]);
  const handleFilterChange = useCallback((values) => onFilterChange?.(values), [onFilterChange]);

  const pickedRows = useMemo(() => {
    if (isSingle) return rows.filter((row) => row.id === pickedId);
    return rows.filter((row) => checkedIds.includes(row.id));
  }, [isSingle, rows, pickedId, checkedIds]);

  // The rows travel whole, with the unit each was read in — a caller's inline table needs both.
  const handleConfirm = useCallback(() => {
    if (pickedRows.length === 0) return;
    const unitTypes = Object.fromEntries(
      pickedRows.map((row) => [row.id, resolveRowUnitType(row, selections)]),
    );
    onConfirm?.(pickedRows, unitTypes);
  }, [pickedRows, selections, onConfirm]);

  const columns = useMemo(() => {
    const dataColumns = buildColumns(t, {
      unitHeaderSelect,
      onOpenDiscrepancy: buildDiscrepancy ? handleOpenDiscrepancy : undefined,
    });
    if (!isSingle) return dataColumns;
    return [buildPickColumn({ name, selectedId: pickedId, onSelect: handlePick }), ...dataColumns];
  }, [
    buildColumns,
    t,
    unitHeaderSelect,
    buildDiscrepancy,
    handleOpenDiscrepancy,
    isSingle,
    name,
    pickedId,
    handlePick,
  ]);

  const emptyState = useMemo(() => ({ title: t(emptyKey) }), [t, emptyKey]);
  // Optional sections (Suggestion / Other, Linked / Other) — see components/common/pickerGroups.js.
  const groups = useMemo(() => buildGroups?.(t) ?? EMPTY_LIST, [buildGroups, t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const overLimit = !isSingle && maxSelected != null && checkedIds.length > maxSelected;

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
      {
        label: t(confirmKey),
        variant: ButtonVariants.primary,
        disabled: pickedRows.length === 0 || overLimit,
        onClick: handleConfirm,
      },
    ],
    [t, confirmKey, pickedRows.length, overLimit, handleClose, handleConfirm],
  );

  const discrepancyUnitType = discrepancyRow && resolveRowUnitType(discrepancyRow, selections);
  const discrepancy = useMemo(
    () => (discrepancyRow ? buildDiscrepancy?.(t, discrepancyRow, discrepancyUnitType) : null),
    [buildDiscrepancy, t, discrepancyRow, discrepancyUnitType],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.large}
      title={t(titleKey)}
      buttons={buttons}
    >
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          key={`selection-${session}`}
          columns={columns}
          data={rows}
          total={total}
          loading={loading}
          getRowId={getRowId}
          searchable
          onSearchChange={handleSearchChange}
          filters={filters}
          onFilterChange={filters ? handleFilterChange : undefined}
          onSortChange={onSortChange}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          selectable={!isSingle}
          onSelectionChange={handleSelectionChange}
          emptyState={emptyState}
          labels={tableLabels}
          groups={groups}
          getRowGroup={getRowGroup}
        />
      </UnitTypeContext.Provider>

      {discrepancy ? (
        <DiscrepancyModal
          open={discrepancyRow != null}
          onClose={handleCloseDiscrepancy}
          referenceId={discrepancy.referenceId}
          message={discrepancy.message}
          discrepancies={discrepancy.discrepancies}
          sections={discrepancy.sections}
        />
      ) : null}
    </Modal>
  );
};
